// VehiclesPage — maestro de vehículos.
//
// El Excel de planificación trae las placas; este maestro permite al
// admin asignar un chofer a cada vehículo. Cuando se asigna chofer:
//   1. vehicles.current_driver_user_id se actualiza
//   2. routes pendientes (status planning/ready) del vehículo se
//      reasignan automáticamente al nuevo chofer (cascade server-side)
//   3. Si el chofer va al mobile y entra con la placa, puede operar
//
// Tabla con búsqueda + filtro por almacén + chofer asignado / sin asignar.
// Click en una fila abre un drawer con select de chofer.

import { useEffect, useMemo, useState } from "react";
import { useMastersStore } from "../data/mastersStore";
import { api } from "../data/api";
import type { User, Vehicle } from "../data/schemaTypes";
import { Badge, Button, Card, Input, Select } from "../components/ui";
import { DriverEditModal } from "../features/drivers/DriverEditModal";

export function VehiclesPage() {
  const vehicles = useMastersStore((s) => s.vehicles);
  const warehouses = useMastersStore((s) => s.warehouses);
  const users = useMastersStore((s) => s.users);
  const hydrate = useMastersStore((s) => s.hydrate);

  const drivers = useMemo(
    () => users.filter((u) => u.role === "driver" && u.active),
    [users],
  );
  const driverById = useMemo(() => {
    const m = new Map<string, User>();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);
  const warehouseById = useMemo(() => {
    const m = new Map<string, string>();
    warehouses.forEach((w) => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("");
  const [assignmentFilter, setAssignmentFilter] = useState<"all" | "with" | "without">("all");
  const [editing, setEditing] = useState<Vehicle | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toUpperCase();
    return vehicles.filter((v) => {
      if (warehouseFilter && v.warehouse_id !== warehouseFilter) return false;
      if (assignmentFilter === "with" && !v.current_driver_user_id) return false;
      if (assignmentFilter === "without" && v.current_driver_user_id) return false;
      if (!q) return true;
      const driver = v.current_driver_user_id ? driverById.get(v.current_driver_user_id) : null;
      return (
        v.plate.toUpperCase().includes(q) ||
        (driver?.full_name ?? "").toUpperCase().includes(q)
      );
    });
  }, [vehicles, search, warehouseFilter, assignmentFilter, driverById]);

  const withoutCount = useMemo(
    () => vehicles.filter((v) => !v.current_driver_user_id).length,
    [vehicles],
  );

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Vehículos</h1>
          <p className="text-xs text-sub mt-0.5">
            {rows.length} de {vehicles.length} vehículos
            {withoutCount > 0 && (
              <>
                {" · "}
                <span className="text-state-failed">
                  {withoutCount} sin chofer
                </span>
              </>
            )}
          </p>
        </div>
      </header>

      <Card padding="sm" className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Buscar"
            placeholder="Placa o chofer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Almacén"
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
          >
            <option value="">Todos</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-44">
          <Select
            label="Asignación"
            value={assignmentFilter}
            onChange={(e) => setAssignmentFilter(e.target.value as typeof assignmentFilter)}
          >
            <option value="all">Todos</option>
            <option value="with">Con chofer</option>
            <option value="without">Sin chofer</option>
          </Select>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-page text-sub border-b border-border">
              <tr className="text-left">
                <Th>Placa</Th>
                <Th>Almacén</Th>
                <Th>Chofer asignado</Th>
                <Th>Estado</Th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const driver = v.current_driver_user_id
                  ? driverById.get(v.current_driver_user_id)
                  : null;
                return (
                  <tr
                    key={v.id}
                    className="border-b border-border last:border-b-0 hover:bg-page/60"
                  >
                    <td className="px-3 py-2 font-mono font-semibold text-ink">
                      {v.plate}
                    </td>
                    <td className="px-3 py-2 text-ink">
                      {warehouseById.get(v.warehouse_id) ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-ink">
                      {driver ? (
                        <span>{driver.full_name}</span>
                      ) : (
                        <span className="text-state-failed text-xs">Sin asignar</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={v.active ? "delivered" : "neutral"}>
                        {v.active ? "Activo" : "Inactivo"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setEditing(v)}
                        className="text-xs text-primary hover:underline px-1"
                      >
                        Asignar chofer
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-sub">
                    No hay vehículos que coincidan con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <AssignDriverDrawer
          vehicle={editing}
          drivers={drivers}
          warehouseName={warehouseById.get(editing.warehouse_id) ?? ""}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            // Refrescamos masters para tomar el current_driver_user_id nuevo.
            await hydrate();
          }}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}

// ---------- Drawer: asignar chofer al vehículo ----------

function AssignDriverDrawer({
  vehicle,
  drivers,
  warehouseName,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle;
  drivers: User[];
  warehouseName: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [selected, setSelected] = useState<string>(
    vehicle.current_driver_user_id ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sub-modal: "+ Nuevo chofer" para crear y asignar en un solo flujo.
  const [creatingNew, setCreatingNew] = useState(false);

  const currentName = vehicle.current_driver_user_id
    ? drivers.find((d) => d.id === vehicle.current_driver_user_id)?.full_name ??
      "(chofer no encontrado)"
    : null;

  async function save() {
    setError(null);
    setSaving(true);
    try {
      await api.setVehicleDriver(
        vehicle.id,
        selected === "" ? null : selected,
      );
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // ESC cierra (pero no si hay sub-modal de "Nuevo chofer" arriba —
  // ese maneja su propio cierre).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (creatingNew) return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, creatingNew]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <aside
        className="relative w-[420px] max-w-full bg-card h-full shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-border flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-sub">
              Vehículo
            </div>
            <div className="font-mono font-semibold text-lg text-ink">
              {vehicle.plate}
            </div>
            {warehouseName && (
              <div className="text-xs text-sub mt-0.5">{warehouseName}</div>
            )}
          </div>
          <button onClick={onClose} className="text-sub hover:text-ink p-1" title="Cerrar">
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-sub mb-1">
              Chofer actualmente asignado
            </div>
            {currentName ? (
              <div className="text-sm text-ink">{currentName}</div>
            ) : (
              <div className="text-sm text-state-failed italic">Sin chofer asignado</div>
            )}
          </div>

          <div>
            <Select
              label="Asignar a chofer existente"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">— Sin chofer —</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.full_name} {d.email && `· ${d.email}`}
                </option>
              ))}
            </Select>
            <p className="mt-2 text-[11px] text-sub leading-snug">
              Al asignar, las rutas pendientes (programadas y de hoy) del
              vehículo se reasignan automáticamente al chofer.
            </p>
          </div>

          {/* Divider "— o —" entre las dos opciones de asignación.
              Visualmente separa "elegir existente" de "crear nuevo" para
              que el admin entienda que son dos caminos paralelos. */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] uppercase tracking-wider text-sub font-medium">
              o
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* CTA destacado para crear chofer nuevo. Borde dashed + bg
              tenue primary + ícono circular → mucho más visible que un
              ghost button chiquito al lado del texto. */}
          <div>
            <button
              type="button"
              onClick={() => setCreatingNew(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary text-primary font-semibold text-sm transition-colors group"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-base font-bold leading-none group-hover:scale-110 transition-transform">
                +
              </span>
              <span>Crear nuevo chofer y asignarlo</span>
            </button>
            <p className="mt-2 text-[11px] text-sub leading-snug">
              Se abrirá un formulario corto. Al guardar, el chofer queda
              creado <strong>y asignado a este vehículo</strong> en un solo paso.
            </p>
          </div>

          {drivers.length === 0 && (
            <div className="bg-state-delayed/10 border border-state-delayed/30 rounded p-3 text-xs text-state-delayed">
              No hay choferes cargados todavía — usá el botón de arriba para
              crear el primero y asignarlo a este vehículo.
            </div>
          )}

          {error && (
            <div className="bg-state-failed/10 border border-state-failed/30 rounded p-3 text-xs text-state-failed">
              {error}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={save}
            disabled={saving || selected === (vehicle.current_driver_user_id ?? "")}
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </footer>
      </aside>

      {/* Sub-modal: crear chofer y asignarlo a este vehículo en un paso.
          La asignación la hace el propio DriverEditModal vía
          presetVehicleId → api.setVehicleDriver. Al cerrar, el drawer
          ya tiene el chofer asignado; refrescamos masters + cerramos. */}
      {creatingNew && (
        <DriverEditModal
          driver={null}
          mode="create"
          presetVehicleId={vehicle.id}
          onCreated={async () => {
            // El chofer ya quedó asignado al vehículo. Refrescamos
            // masters y cerramos el drawer entero — el caller de
            // AssignDriverDrawer (VehiclesPage) hace su propio hydrate
            // en onSaved.
            await onSaved();
          }}
          onClose={() => setCreatingNew(false)}
        />
      )}
    </div>
  );
}
